import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  CalendarCheck2,
  Check,
  ChevronDown,
  CircleDollarSign,
  Clock3,
  MessageCircle,
  Send,
  Sparkles,
  UserRoundCheck,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/posthog";
import { trackPixelEvent } from "@/lib/meta-pixel";
import "@/empty-slot-revenue.css";
import "@/empty-slot-revenue-refinement.css";

type FormState = "idle" | "loading" | "success" | "error";
type SupabaseError = { code?: string; message?: string } | null;

const LOGO_SRC = "/uploads/figgy_icon_transparent.png";
const providers = ["GlossGenius", "Vagaro", "Square", "Fresha", "Booksy", "Acuity"];
const scrollingProviders = [...providers, ...providers];

const processSteps = [
  { number: "01", title: "Text the opening", body: "Send the service, time and price. Nothing new to learn.", icon: MessageCircle },
  { number: "02", title: "Approve the clients", body: "You choose exactly which past clients Figgy may contact.", icon: UserRoundCheck },
  { number: "03", title: "Deposit paid. Slot filled.", body: "The first paid deposit secures the opening and you receive the booking details.", icon: CircleDollarSign },
];

const transformationSteps = [
  { title: "A cancellation appears", body: "An empty appointment puts real revenue at risk.", icon: Clock3 },
  { title: "The right clients hear about it", body: "Selected past clients receive a personal, time-limited offer.", icon: Send },
  { title: "A deposit confirms the booking", body: "Interest becomes commitment before the opening disappears.", icon: CalendarCheck2 },
];

const faqs = [
  {
    question: "Do I have to replace my booking system?",
    answer: "No. Figgy works alongside GlossGenius, Vagaro, Square, Fresha, Booksy, Acuity, or the calendar you already use.",
  },
  {
    question: "Who receives the messages?",
    answer: "Only past clients you select from your consented customer list. You approve every audience before any message is sent.",
  },
  {
    question: "What does the first opening cost?",
    answer: "Your first opening is free to try. There is no card required and no calendar migration.",
  },
  {
    question: "What happens when more than one client wants it?",
    answer: "The first client to pay the deposit secures the opening. Everyone else is told that the slot has been filled.",
  },
];

function FiggyMark() {
  return (
    <a className="es-logo" href="#top" aria-label="Figgy home">
      <span className="es-logo-icon" aria-hidden="true">
        <img src={LOGO_SRC} alt="" width={28} height={28} loading="eager" decoding="sync" />
      </span>
      <span>Figgy</span>
    </a>
  );
}

function Reveal({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : { opacity: 0, y: 22 }}
      whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.62, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function WaitlistForm({ location, compact = false }: { location: string; compact?: boolean }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<FormState>("idle");
  const [message, setMessage] = useState("");
  const submittingRef = useRef(false);
  const focusedRef = useRef(false);
  const typedRef = useRef(false);
  const inputId = `early-access-email-${location}`;
  const messageId = `early-access-message-${location}`;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current || state === "success") return;

    trackEvent("empty_slot_form_submit_clicked", { form_location: location });
    const normalizedEmail = email.trim().toLowerCase();

    if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      trackEvent("empty_slot_form_validation_failed", { form_location: location, reason: "invalid_email" });
      setState("error");
      setMessage("Enter a valid email address.");
      return;
    }

    submittingRef.current = true;
    setState("loading");
    setMessage("");

    try {
      const params = new URLSearchParams(window.location.search);
      const { error } = (await (supabase as any).from("marketing_leads").insert({
        email: normalizedEmail,
        source: `empty_slot_revenue_waitlist_${location}`,
        utm_source: params.get("utm_source"),
        utm_campaign: params.get("utm_campaign"),
      })) as { error: SupabaseError };

      const isDuplicate = error?.code === "23505";
      if (error && !isDuplicate) throw error;

      trackEvent("empty_slot_waitlist_joined", {
        form_location: location,
        entry_path: window.location.pathname,
        is_duplicate: isDuplicate,
      });

      if (!isDuplicate) {
        trackPixelEvent("Lead", { content_name: "Figgy cancellation fill", form_location: location });
      }

      const onboardingParams = new URLSearchParams({ source: "empty_slot_revenue_waitlist" });
      ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((key) => {
        const value = params.get(key);
        if (value) onboardingParams.set(key, value);
      });

      setState("success");
      setMessage("Saved. Taking you to Figgy…");
      window.setTimeout(() => {
        trackEvent("empty_slot_onboarding_redirected", {
          form_location: location,
          entry_path: window.location.pathname,
          is_duplicate: isDuplicate,
        });
        navigate(`/magic-onboarding-v2?${onboardingParams.toString()}`);
      }, 350);
    } catch (error) {
      console.error("Waitlist submission failed", error);
      trackEvent("empty_slot_form_submission_failed", { form_location: location });
      setState("error");
      setMessage("That didn’t go through. Please try again.");
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <form className={`es-waitlist-form ${compact ? "is-compact" : ""}`} onSubmit={submit} noValidate>
      <label className="es-sr-only" htmlFor={inputId}>Email address</label>
      <div className="es-input-shell">
        <input
          id={inputId}
          type="email"
          name="email"
          required
          inputMode="email"
          autoComplete="email"
          value={email}
          onFocus={() => {
            if (!focusedRef.current) {
              focusedRef.current = true;
              trackEvent("empty_slot_email_focused", { form_location: location });
            }
          }}
          onChange={(event) => {
            setEmail(event.target.value);
            if (!typedRef.current && event.target.value.length > 0) {
              typedRef.current = true;
              trackEvent("empty_slot_email_started", { form_location: location });
            }
            if (state === "error") {
              setState("idle");
              setMessage("");
            }
          }}
          placeholder="you@yourstudio.com"
          aria-invalid={state === "error"}
          aria-describedby={message ? messageId : undefined}
          disabled={state === "loading" || state === "success"}
        />
        <button type="submit" disabled={state === "loading" || state === "success"} aria-label="Fill my next cancellation">
          <span>{state === "loading" ? "Saving…" : state === "success" ? "Saved" : "Fill my next cancellation"}</span>
          <ArrowRight size={18} aria-hidden="true" />
        </button>
      </div>
      {message ? <p id={messageId} className={`es-form-message ${state === "error" ? "is-error" : ""}`} role={state === "error" ? "alert" : "status"}>{message}</p> : null}
      <p className="es-form-note">Your first opening is free. No card required. Keep your current booking system.</p>
    </form>
  );
}

function RevenueDemo() {
  const reduceMotion = useReducedMotion();
  const [phase, setPhase] = useState(reduceMotion ? 4 : 0);

  useEffect(() => {
    if (reduceMotion) return;
    let stopped = false;
    let timers: number[] = [];
    const run = () => {
      if (stopped) return;
      setPhase(0);
      timers = [
        window.setTimeout(() => setPhase(1), 700),
        window.setTimeout(() => setPhase(2), 1500),
        window.setTimeout(() => setPhase(3), 2350),
        window.setTimeout(() => setPhase(4), 3200),
        window.setTimeout(run, 7200),
      ];
    };
    run();
    return () => { stopped = true; timers.forEach(window.clearTimeout); };
  }, [reduceMotion]);

  return (
    <div className="es-demo-wrap" aria-label="Animated example of Figgy filling a cancellation">
      <motion.div className="es-message-demo" initial={reduceMotion ? false : { opacity: 0, y: 24 }} animate={reduceMotion ? undefined : { opacity: 1, y: 0 }} transition={{ duration: 0.76, delay: 0.14 }}>
        <div className="es-message-stack" aria-hidden="true">
          <div className="es-message-row is-outgoing"><div className="es-imessage-bubble es-imessage-provider"><span>Fill Thursday at 2:00 PM.</span><strong>Balayage · $180</strong></div></div>
          <div className={`es-message-row ${phase >= 1 ? "is-visible" : ""}`}><div className="es-message-avatar">F</div><div className="es-imessage-bubble es-imessage-figgy">Sending this opening to the clients you approved.</div></div>
          <div className={`es-message-row ${phase >= 2 ? "is-visible" : ""}`}><div className="es-message-avatar is-client">M</div><div className="es-imessage-bubble es-imessage-client">I can make 2 PM.</div></div>
          <div className={`es-message-row es-result-row ${phase >= 3 ? "is-visible" : ""}`}><div className="es-result-bubble is-deposit"><span className="es-result-icon"><Check size={17} strokeWidth={3} /></span><div><small>Deposit paid</small><strong>$40 collected</strong></div></div></div>
          <div className={`es-message-row es-result-row ${phase >= 4 ? "is-visible" : ""}`}><div className="es-result-bubble is-revenue"><span className="es-result-icon"><Sparkles size={17} /></span><div><small>Opening filled</small><strong>+$180 recovered</strong></div></div></div>
        </div>
      </motion.div>
    </div>
  );
}

export default function EmptySlotRevenueLandingRefined() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Figgy — Fill Your Next Cancellation";
    trackEvent("empty_slot_landing_viewed", { entry_path: window.location.pathname, audience: "hair_beauty" });
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <main className="es-page" id="top">
      <nav className="es-nav" aria-label="Main navigation"><div className="es-nav-inner"><FiggyMark /><div className="es-nav-links"><a href="#how-it-works">How it works</a><a href="#faq">FAQ</a></div><a className="es-nav-cta" href="#early-access" onClick={() => trackEvent("empty_slot_nav_cta_clicked", { cta_location: "nav" })}>Fill my next cancellation <ArrowRight size={15} /></a></div></nav>

      <section className="es-hero" aria-labelledby="hero-title">
        <div className="es-hero-glow" aria-hidden="true" />
        <div className="es-container es-hero-grid">
          <div className="es-hero-copy">
            <motion.p className="es-eyebrow" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>For hairstylists, barbers, and salon-suite owners</motion.p>
            <motion.h1 id="hero-title" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.76, delay: 0.05 }}><span>A last-minute cancellation</span><span>shouldn’t cost you $180.</span></motion.h1>
            <motion.p className="es-hero-subhead" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.68, delay: 0.12 }}>Text Figgy the empty appointment. We offer it to past clients you approve, collect the deposit, and send you the confirmed booking.</motion.p>
            <motion.div className="es-hero-form" id="early-access" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.68, delay: 0.19 }}><WaitlistForm location="hero" /></motion.div>
            <motion.div className="es-reassurance" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }}>
              <span><Check size={15} /> You approve every client contacted</span>
              <span><Check size={15} /> Your first opening is free</span>
              <span><Check size={15} /> Keep GlossGenius, Vagaro, Square, or Fresha</span>
            </motion.div>
          </div>
          <RevenueDemo />
        </div>
      </section>

      <section className="es-compatibility" aria-labelledby="compatibility-title"><div className="es-container es-compatibility-inner"><div><h2 id="compatibility-title">Keep the booking system you already use.</h2><p>Figgy works alongside your existing calendar. No migration required.</p></div><div className="es-provider-marquee"><div className="es-provider-track">{scrollingProviders.map((provider, index) => <span key={`${provider}-${index}`}>{provider}</span>)}</div></div></div></section>

      <section className="es-problem-section" aria-labelledby="problem-title"><div className="es-container"><Reveal className="es-section-intro"><p className="es-kicker">The opening is urgent</p><h2 id="problem-title">Instagram shows the cancellation to everyone. Figgy shows it to the clients most likely to take it.</h2><p>You choose the audience. Figgy sends the offer personally, stops when the deposit is paid, and gives you the confirmed booking.</p></Reveal><div className="es-transformation-grid">{transformationSteps.map((step, index) => { const Icon = step.icon; return <Reveal className="es-transformation-card" delay={index * 0.08} key={step.title}><span className="es-card-number">0{index + 1}</span><span className="es-card-icon"><Icon size={21} /></span><h3>{step.title}</h3><p>{step.body}</p>{index < 2 ? <ArrowRight className="es-stage-arrow" size={20} /> : null}</Reveal>; })}</div></div></section>

      <section className="es-how-section" id="how-it-works" aria-labelledby="how-title"><div className="es-container"><Reveal className="es-section-intro es-section-intro-centered"><p className="es-kicker">How it works</p><h2 id="how-title">One text in. One empty slot recovered.</h2></Reveal><div className="es-process-grid">{processSteps.map((step, index) => { const Icon = step.icon; return <Reveal className="es-process-card" delay={index * 0.08} key={step.number}><div className="es-process-top"><span>{step.number}</span><span className="es-process-icon"><Icon size={22} /></span></div><h3>{step.title}</h3><p>{step.body}</p></Reveal>; })}</div></div></section>

      <section className="es-dark-section" aria-labelledby="difference-title"><div className="es-dark-glow" /><div className="es-container es-dark-grid"><Reveal className="es-dark-copy"><p className="es-kicker es-kicker-light">A hands-on first result</p><h2 id="difference-title">We are personally helping the first providers fill their next cancellation.</h2><div className="es-benefit-list"><span><Check size={17} /> No card or subscription to start</span><span><Check size={17} /> You approve every message and recipient</span><span><Check size={17} /> First paid deposit gets the opening</span></div></Reveal><Reveal className="es-status-card" delay={0.1}><div className="es-status-card-head"><div><span>Opening status</span><strong>Thursday · 2:00 PM</strong></div><span className="es-live-dot">Booked</span></div><div className="es-status-service"><span>Balayage</span><strong>$180</strong></div><div className="es-status-timeline"><div className="is-complete"><span><Check size={13} /></span><p><strong>Opening received</strong><small>Service, time and price</small></p></div><div className="is-complete"><span><Check size={13} /></span><p><strong>Approved clients contacted</strong><small>Personal, time-limited offer</small></p></div><div className="is-complete"><span><Check size={13} /></span><p><strong>Deposit paid</strong><small>Booking confirmed</small></p></div></div><div className="es-status-result"><span>Recovered appointment value</span><strong>+$180</strong></div></Reveal></div></section>

      <section className="es-faq-section" id="faq" aria-labelledby="faq-title"><div className="es-container es-faq-grid"><Reveal className="es-faq-heading"><p className="es-kicker">Questions, answered</p><h2 id="faq-title">Keep your calendar. Fill more of it.</h2></Reveal><div className="es-faq-list">{faqs.map((faq, index) => <Reveal delay={index * 0.045} key={faq.question}><details className="es-faq-item" onToggle={(event) => { if ((event.currentTarget as HTMLDetailsElement).open) trackEvent("empty_slot_faq_opened", { question: faq.question }); }}><summary><span>{faq.question}</span><ChevronDown size={19} /></summary><p>{faq.answer}</p></details></Reveal>)}</div></div></section>

      <section className="es-final-cta" aria-labelledby="final-title"><div className="es-container"><Reveal className="es-final-card"><div className="es-final-copy"><p className="es-kicker">Your next cancellation is the test</p><h2 id="final-title">Let Figgy try to fill it for free.</h2><p>No card. No calendar migration. You approve every client contacted.</p></div><div className="es-final-form"><WaitlistForm location="final" compact /></div></Reveal></div></section>

      <footer className="es-footer"><div className="es-container es-footer-inner"><FiggyMark /><p>Fill last-minute openings without changing your booking system.</p><div><a href="/legal/privacy-policy">Privacy</a><a href="/legal/terms-and-conditions">Terms</a></div></div></footer>
    </main>
  );
}
